import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "url",
    "httpMethod",
    "headersContainer",
    "attributesContainer",
    "hiddenField",
    "recordPickers",
    "previewOutput",
    "testResult",
  ];
  static values = {
    recordsUrl: String,
    fieldsUrl: String,
    previewUrl: String,
    testUrl: String,
  };

  declare urlTarget: HTMLInputElement;
  declare httpMethodTarget: HTMLSelectElement;
  declare headersContainerTarget: HTMLElement;
  declare attributesContainerTarget: HTMLElement;
  declare hiddenFieldTarget: HTMLInputElement;
  declare recordsUrlValue: string;
  declare fieldsUrlValue: string;
  declare previewUrlValue: string;
  declare testUrlValue: string;
  declare hasRecordPickersTarget: boolean;
  declare recordPickersTarget: HTMLElement;
  declare hasPreviewOutputTarget: boolean;
  declare previewOutputTarget: HTMLElement;
  declare hasTestResultTarget: boolean;
  declare testResultTarget: HTMLElement;

  private availableRecords: Array<{ value: string; label: string }> = [];

  connect() {
    this.loadAvailableRecords();
    this.serialize();
    this.element.closest("form")?.addEventListener("submit", () =>
      this.serialize()
    );
  }

  async loadAvailableRecords() {
    if (!this.recordsUrlValue) return;
    try {
      const response = await fetch(this.recordsUrlValue, {
        headers: { Accept: "application/json" },
      });
      const data = await response.json();
      this.availableRecords = [
        ...(data.event || []),
        ...(data.primary || []),
        ...(data.profile || []),
      ];
      this.populateRecordSelects();
      this.buildRecordPickers();
    } catch {
      // silently fail — records can be typed manually
    }
  }

  populateRecordSelects() {
    this.attributesContainerTarget
      .querySelectorAll<HTMLSelectElement>("[data-record-select]")
      .forEach((select) => {
        const current = select.value;
        const hasOptions = select.options.length > 1;
        if (hasOptions) return;

        select.innerHTML = '<option value="">Select record...</option>';
        this.availableRecords.forEach((r) => {
          const opt = document.createElement("option");
          opt.value = r.value;
          opt.textContent = r.label;
          if (r.value === current) opt.selected = true;
          select.appendChild(opt);
        });
      });
  }

  buildRecordPickers() {
    if (!this.hasRecordPickersTarget) return;

    const pickableRecords = this.availableRecords.filter(
      (r) => r.value !== "user_profile"
    );
    if (pickableRecords.length === 0) return;

    pickableRecords.forEach((record) => {
      const group = document.createElement("div");
      group.className = "form-group row";
      group.innerHTML = `
        <div class="col-4">
          <label class="col-form-label">${record.label}</label>
        </div>
        <div class="col-8">
          <input class="form-control" type="number" placeholder="Enter ${record.label} ID" data-preview-record="${record.value}">
        </div>
      `;
      this.recordPickersTarget.appendChild(group);
    });
  }

  addHeader() {
    const row = document.createElement("div");
    row.className = "row mb-2";
    row.innerHTML = `
      <div class="col-5">
        <input class="form-control" type="text" placeholder="Header name" data-header-key>
      </div>
      <div class="col-5">
        <input class="form-control" type="text" placeholder="Header value" data-header-value>
      </div>
      <div class="col-2">
        <button class="btn btn-falcon-danger btn-sm" type="button" data-action="click->webhook-config#removeRow">
          <i class="fa fa-trash"></i>
        </button>
      </div>
    `;
    this.headersContainerTarget.appendChild(row);
  }

  addAttribute() {
    const row = document.createElement("div");
    row.className = "row mb-2";
    row.innerHTML = `
      <div class="col-5">
        <select class="form-control" data-record-select data-action="change->webhook-config#recordChanged">
          <option value="">Select record...</option>
        </select>
      </div>
      <div class="col-5">
        <select class="form-control" data-field-select>
          <option value="">Select field...</option>
        </select>
      </div>
      <div class="col-2">
        <button class="btn btn-falcon-danger btn-sm" type="button" data-action="click->webhook-config#removeRow">
          <i class="fa fa-trash"></i>
        </button>
      </div>
    `;
    this.attributesContainerTarget.appendChild(row);
    this.populateRecordSelects();
  }

  removeRow(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const row = button.closest(".row");
    row?.remove();
    this.serialize();
  }

  async recordChanged(event: Event) {
    const select = event.currentTarget as HTMLSelectElement;
    const record = select.value;
    const row = select.closest(".row");
    const fieldSelect = row?.querySelector<HTMLSelectElement>(
      "[data-field-select]"
    );
    if (!fieldSelect || !record || !this.fieldsUrlValue) return;

    try {
      const url = `${this.fieldsUrlValue}?record=${encodeURIComponent(record)}`;
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      const fields: Array<{ name: string; type: string; label?: string }> =
        await response.json();
      fieldSelect.innerHTML = '<option value="">Select field...</option>';
      fields.forEach((f) => {
        const opt = document.createElement("option");
        opt.value = f.name;
        opt.textContent = `${f.label || f.name} (${f.type})`;
        fieldSelect.appendChild(opt);
      });
    } catch {
      // silently fail
    }
    this.serialize();
  }

  private collectRecordIds(): Record<string, string> {
    const records: Record<string, string> = {};
    this.element
      .querySelectorAll<HTMLInputElement>("[data-preview-record]")
      .forEach((input) => {
        const name = input.dataset.previewRecord;
        if (name && input.value) {
          records[name] = input.value;
        }
      });
    return records;
  }

  async previewPayload() {
    if (!this.previewUrlValue || !this.hasPreviewOutputTarget) return;

    this.previewOutputTarget.innerHTML =
      '<p class="text-muted">Loading...</p>';

    try {
      const csrfToken = document
        .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.getAttribute("content");
      const records = this.collectRecordIds();

      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({ records }),
      });

      const data = await response.json();
      this.previewOutputTarget.innerHTML = `
        <h6>Payload Preview</h6>
        <pre class="bg-light p-3 rounded"><code>${JSON.stringify(data.payload, null, 2)}</code></pre>
      `;
    } catch (e) {
      this.previewOutputTarget.innerHTML = `
        <div class="alert alert-danger">Failed to generate preview: ${e instanceof Error ? e.message : "Unknown error"}</div>
      `;
    }
  }

  async sendTestWebhook() {
    if (!this.testUrlValue || !this.hasTestResultTarget) return;

    this.testResultTarget.innerHTML =
      '<p class="text-muted">Sending test webhook...</p>';

    try {
      const csrfToken = document
        .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.getAttribute("content");
      const records = this.collectRecordIds();

      const response = await fetch(this.testUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({ records }),
      });

      const data = await response.json();
      const isSuccess = data.status >= 200 && data.status < 300;
      const alertClass = isSuccess ? "alert-success" : "alert-danger";

      this.testResultTarget.innerHTML = `
        <div class="alert ${alertClass}">
          <strong>Response status: ${data.status}</strong>
          <pre class="mt-2 mb-0 bg-light p-2 rounded"><code>${data.body || "(empty)"}</code></pre>
        </div>
      `;
    } catch (e) {
      this.testResultTarget.innerHTML = `
        <div class="alert alert-danger">Failed to send test webhook: ${e instanceof Error ? e.message : "Unknown error"}</div>
      `;
    }
  }

  serialize() {
    const config: {
      url: string;
      http_method: string;
      headers: Record<string, string>;
      attributes: Array<{ record: string; field: string }>;
    } = {
      url: this.urlTarget.value,
      http_method: this.httpMethodTarget.value,
      headers: {},
      attributes: [],
    };

    this.headersContainerTarget.querySelectorAll(".row").forEach((row) => {
      const key = row.querySelector<HTMLInputElement>(
        "[data-header-key]"
      )?.value;
      const value = row.querySelector<HTMLInputElement>(
        "[data-header-value]"
      )?.value;
      if (key && value) {
        config.headers[key] = value;
      }
    });

    this.attributesContainerTarget.querySelectorAll(".row").forEach((row) => {
      const record = row.querySelector<HTMLSelectElement>(
        "[data-record-select]"
      )?.value;
      const field = row.querySelector<HTMLSelectElement>(
        "[data-field-select]"
      )?.value;
      if (record && field) {
        config.attributes.push({ record, field });
      }
    });

    this.hiddenFieldTarget.value = JSON.stringify(config);
  }
}
